import { apiJsonHeaders, withApiLocale } from "../utils/apiLocale";
import { STORAGE_KEY as AUTH_STORAGE_KEY } from "../lib/AuthContext";

const BASE_URL = "/api/inventory";

// Quién opera el inventario: el backend lo usa para dejar que una secretaria
// trabaje sobre el inventario de sus profesionales (y nada más).
const actingUserId = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || "null")?.id || "";
  } catch {
    return "";
  }
};
const inventoryHeaders = () => apiJsonHeaders({ "X-Acting-User-Id": actingUserId() });

const handle = async (res, fallback) => {
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || fallback);
  }
  return res.json();
};

// ---- Inventarios que puede operar el usuario (secretaria -> sus profesionales) ----
export const fetchInventoryOwners = async (userId) => {
  const res = await fetch(`${BASE_URL}/owners/${userId}`, {
    method: "GET",
    headers: inventoryHeaders(),
  });
  return handle(res, "Error al obtener los inventarios disponibles");
};

// ---- Monedas / settings ----
export const fetchInventorySettings = async (ownerId) => {
  const res = await fetch(`${BASE_URL}/settings/${ownerId}`, {
    method: "GET",
    headers: inventoryHeaders(),
  });
  return handle(res, "Error al obtener la configuración de monedas");
};

export const saveInventorySettings = async (ownerId, fields) => {
  const res = await fetch(`${BASE_URL}/settings/${ownerId}`, {
    method: "PUT",
    headers: inventoryHeaders(),
    body: JSON.stringify(withApiLocale(fields)),
  });
  return handle(res, "Error al guardar la configuración de monedas");
};

// ---- Insumos ----
export const fetchSupplies = async (ownerId, q = "") => {
  const url = q
    ? `${BASE_URL}/supplies/${ownerId}?q=${encodeURIComponent(q)}`
    : `${BASE_URL}/supplies/${ownerId}`;
  const res = await fetch(url, { method: "GET", headers: inventoryHeaders() });
  return handle(res, "Error al obtener los insumos");
};

export const fetchReorderSupplies = async (ownerId) => {
  const res = await fetch(`${BASE_URL}/supplies/${ownerId}/reorder`, {
    method: "GET",
    headers: inventoryHeaders(),
  });
  return handle(res, "Error al obtener los insumos a reponer");
};

// Insumos por vencer (alerta pre-vencimiento alcanzada o ya vencidos). `today` = día local.
export const fetchExpiringSupplies = async (ownerId, today) => {
  const url = today
    ? `${BASE_URL}/supplies/${ownerId}/expiring?today=${encodeURIComponent(today)}`
    : `${BASE_URL}/supplies/${ownerId}/expiring`;
  const res = await fetch(url, { method: "GET", headers: inventoryHeaders() });
  return handle(res, "Error al obtener los insumos por vencer");
};

export const createSupply = async (ownerId, fields) => {
  const res = await fetch(`${BASE_URL}/supplies`, {
    method: "POST",
    headers: inventoryHeaders(),
    body: JSON.stringify(withApiLocale({ ...fields, owner_id: ownerId })),
  });
  return handle(res, "Error al crear el insumo");
};

export const updateSupply = async (id, ownerId, fields) => {
  const res = await fetch(`${BASE_URL}/supplies/${id}`, {
    method: "PUT",
    headers: inventoryHeaders(),
    body: JSON.stringify(withApiLocale({ ...fields, owner_id: ownerId })),
  });
  return handle(res, "Error al actualizar el insumo");
};

export const deleteSupply = async (id, ownerId) => {
  const res = await fetch(
    `${BASE_URL}/supplies/${id}?owner_id=${encodeURIComponent(ownerId)}`,
    { method: "DELETE", headers: inventoryHeaders() }
  );
  return handle(res, "Error al eliminar el insumo");
};

export const fetchSupplyHistory = async (id, ownerId) => {
  const res = await fetch(
    `${BASE_URL}/supply/${id}/history?owner_id=${encodeURIComponent(ownerId)}`,
    { method: "GET", headers: inventoryHeaders() }
  );
  return handle(res, "Error al obtener el historial del insumo");
};

// ---- Tratamientos ----
export const fetchTreatments = async (ownerId) => {
  const res = await fetch(`${BASE_URL}/treatments/${ownerId}`, {
    method: "GET",
    headers: inventoryHeaders(),
  });
  return handle(res, "Error al obtener los tratamientos");
};

export const createTreatment = async (ownerId, fields) => {
  const res = await fetch(`${BASE_URL}/treatments`, {
    method: "POST",
    headers: inventoryHeaders(),
    body: JSON.stringify(withApiLocale({ ...fields, owner_id: ownerId })),
  });
  return handle(res, "Error al crear el tratamiento");
};

export const updateTreatment = async (id, ownerId, fields) => {
  const res = await fetch(`${BASE_URL}/treatments/${id}`, {
    method: "PUT",
    headers: inventoryHeaders(),
    body: JSON.stringify(withApiLocale({ ...fields, owner_id: ownerId })),
  });
  return handle(res, "Error al actualizar el tratamiento");
};

export const deleteTreatment = async (id, ownerId) => {
  const res = await fetch(
    `${BASE_URL}/treatments/${id}?owner_id=${encodeURIComponent(ownerId)}`,
    { method: "DELETE", headers: inventoryHeaders() }
  );
  return handle(res, "Error al eliminar el tratamiento");
};

export const fetchTreatmentStats = async (id, ownerId) => {
  const res = await fetch(
    `${BASE_URL}/treatment/${id}/stats?owner_id=${encodeURIComponent(ownerId)}`,
    { method: "GET", headers: inventoryHeaders() }
  );
  return handle(res, "Error al obtener las estadísticas del tratamiento");
};

// Aplica un tratamiento a un paciente (descuenta stock). items: [{ supply_id, quantity_used }]
export const applyTreatment = async (ownerId, payload) => {
  const res = await fetch(`${BASE_URL}/treatments/apply`, {
    method: "POST",
    headers: inventoryHeaders(),
    body: JSON.stringify(withApiLocale({ ...payload, owner_id: ownerId })),
  });
  return handle(res, "Error al registrar el consumo del tratamiento");
};

// ---- Analytics ----
export const fetchInventoryAnalytics = async (ownerId) => {
  const res = await fetch(`${BASE_URL}/analytics/${ownerId}`, {
    method: "GET",
    headers: inventoryHeaders(),
  });
  return handle(res, "Error al obtener las estadísticas del inventario");
};
